// Piezas base del portal de aliados con el libro de marca Molantur (ver styles/molanturTheme.ts).
// Solo presentación: ninguna llama a servicios ni conoce el contexto del aliado.
import { ButtonHTMLAttributes, CSSProperties, FormEvent, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, useEffect, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { add, format, isSameDay, startOfToday } from "date-fns";
import { es } from "date-fns/locale";
import m, { desktop } from "../../../styles/molanturTheme";
import luna from "../../../assets/ally/picto/luna.png";
import tatio from "../../../assets/ally/picto/tatio.png";
import piedras from "../../../assets/ally/picto/piedras.png";
import cejar from "../../../assets/ally/picto/cejar.png";
import baltinache from "../../../assets/ally/picto/baltinache.png";
import arcoiris from "../../../assets/ally/picto/arcoiris.png";
import astro from "../../../assets/ally/picto/astro.png";
import uyuni from "../../../assets/ally/picto/uyuni.png";
import ticket from "../../../assets/ally/picto/ticket.png";
import RangeCalendar from "./RangeCalendar";

// ── Formatos ────────────────────────────────────────────────────────
export const clp = (n: number) => `$${Math.round(Number(n) || 0).toLocaleString('es-CL')}`
// "jueves 2 de octubre"
export const longDate = (d: Date) => format(d, "EEEE d 'de' MMMM", { locale: es })
// "2 oct"
export const shortDate = (d: Date) => format(d, 'd MMM', { locale: es })
// 'yyyy-MM-dd' → Date local (new Date('2026-10-02') sería UTC y en Chile cae en el día anterior).
export const dateFromIso = (iso: string) => {
    const [y, mo, d] = iso.slice(0, 10).split('-').map(Number)
    return new Date(y, mo - 1, d)
}

// ── Pictograma y tono de cada tour ──────────────────────────────────
// El tour no tiene campo de imagen: se deduce del nombre (el id del tour ES su nombre). Lo que no
// calza con ninguno usa el pictograma genérico.
const PICTOS: { match: string[], key: string, src: string }[] = [
    { match: ['luna'], key: 'luna', src: luna },
    { match: ['tatio', 'geyser', 'geiser'], key: 'tatio', src: tatio },
    { match: ['piedras'], key: 'piedras', src: piedras },
    { match: ['cejar'], key: 'cejar', src: cejar },
    { match: ['baltinache'], key: 'baltinache', src: baltinache },
    { match: ['arcoiris', 'arco iris'], key: 'arcoiris', src: arcoiris },
    { match: ['astro'], key: 'astro', src: astro },
    { match: ['uyuni'], key: 'uyuni', src: uyuni },
]
export function tourVisual(tourId: string) {
    const name = (tourId || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    const found = PICTOS.find(p => p.match.some(word => name.includes(word)))
    const key = found?.key || 'ticket'
    return { picto: found?.src || ticket, tone: m.tones[key] }
}

// ── Iconos (trazo, no imágenes) ─────────────────────────────────────
const ICONS: Record<string, ReactNode> = {
    plus: <path d="M12 5v14M5 12h14" />,
    minus: <path d="M5 12h14" />,
    check: <path d="M4 12.5l5 5L20 6.5" />,
    x: <path d="M6 6l12 12M18 6L6 18" />,
    chevronRight: <path d="M9 5l7 7-7 7" />,
    chevronLeft: <path d="M15 5l-7 7 7 7" />,
    chevronDown: <path d="M5 9l7 7 7-7" />,
    arrowRight: <path d="M5 12h14M13 6l6 6-6 6" />,
    user: <><circle cx="12" cy="8" r="3.6" /><path d="M5.5 19c.7-3.4 3.3-5.2 6.5-5.2s5.8 1.8 6.5 5.2" /></>,
    users: <><circle cx="9" cy="8" r="3.2" /><path d="M3.5 18.5c.6-2.9 2.8-4.5 5.5-4.5s4.9 1.6 5.5 4.5" /><path d="M16 5.2a3.2 3.2 0 010 5.8M17 14c2.2.3 3.9 1.8 4.4 4.3" /></>,
    calendar: <><rect x="4" y="5.5" width="16" height="15" rx="2.5" /><path d="M4 9.5h16M8.5 3.5v4M15.5 3.5v4" /></>,
    camera: <><path d="M4 8.5h3l1.6-2.5h6.8L17 8.5h3v10.5H4z" /><circle cx="12" cy="13.5" r="3.4" /></>,
    logout: <path d="M14 7V5a2 2 0 00-2-2H6a2 2 0 00-2 2v14a2 2 0 002 2h6a2 2 0 002-2v-2M9 12h11M17 8l4 4-4 4" />,
    home: <path d="M4 11l8-6 8 6M6 10v9h12v-9" />,
    wallet: <><rect x="3.5" y="6" width="17" height="13" rx="2.5" /><path d="M3.5 10h17M16 13.5h.01" /></>,
    info: <><circle cx="12" cy="12" r="8.5" /><path d="M12 11v5M12 8h.01" /></>,
    list: <path d="M8.5 6h11M8.5 12h11M8.5 18h11M4 6h.01M4 12h.01M4 18h.01" />,
    download: <path d="M12 4v10M7.5 10.5L12 15l4.5-4.5M5 19.5h14" />,
    search: <><circle cx="11" cy="11" r="6.5" /><path d="M16 16l4.5 4.5" /></>,
    edit: <path d="M14 5l5 5M4 20l1-4L16 5l3 3L8 19l-4 1z" />,
    trash: <path d="M5 7h14M9 7V5h6v2M7 7l1 13h8l1-13" />,
    tool: <path d="M14.5 6.5a4 4 0 00-5.3 5.3L4 17l3 3 5.2-5.2a4 4 0 005.3-5.3l-2.6 2.6-2.4-.6-.6-2.4z" />,
}
export type IconName = keyof typeof ICONS

export function Icon({ name, size = 20, stroke = 1.9, style }: { name: string, size?: number, stroke?: number, style?: CSSProperties }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor"
            strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block', flexShrink: 0, ...style }}>
            {ICONS[name] || null}
        </svg>
    )
}

// ── Botones ─────────────────────────────────────────────────────────
type ButtonKind = 'primary' | 'ghost' | 'quiet' | 'dark' | 'danger'
interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    kind?: ButtonKind
    size?: 'md' | 'lg'
    full?: boolean
    pill?: boolean
    icon?: string
}

const kinds: Record<ButtonKind, ReturnType<typeof css>> = {
    primary: css`background: ${m.color.primary}; color: ${m.color.onPrimary}; box-shadow: ${m.shadow.card};`,
    ghost: css`background: transparent; color: ${m.color.ink}; box-shadow: inset 0 0 0 1.5px ${m.color.lineStrong};`,
    quiet: css`background: ${m.color.surface2}; color: ${m.color.ink};`,
    dark: css`background: ${m.color.ink}; color: ${m.color.surface};`,
    danger: css`background: ${m.color.danger}; color: ${m.color.surface};`,
}

const StyledButton = styled.button<{ $kind: ButtonKind, $size: 'md' | 'lg', $full?: boolean, $pill?: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: ${p => p.$size === 'lg' ? '56px' : '48px'};
    padding: 0 ${p => p.$size === 'lg' ? '22px' : '18px'};
    width: ${p => p.$full ? '100%' : 'auto'};
    border: none;
    border-radius: ${p => p.$pill ? m.radius.pill : m.radius.md};
    font-family: ${m.font.text};
    font-weight: 600;
    font-size: ${p => p.$size === 'lg' ? '16.5px' : '15px'};
    white-space: nowrap;
    cursor: pointer;
    transition: transform .12s ease, opacity .15s ease;
    -webkit-tap-highlight-color: transparent;
    ${p => kinds[p.$kind]}
    &:active { transform: scale(0.97); }
    &:disabled { opacity: 0.45; cursor: not-allowed; }
    &:focus-visible { outline: 2px solid ${m.color.primaryDeep}; outline-offset: 2px; }
`

export function Button({ kind = 'primary', size = 'md', full, pill, icon, children, type = 'button', ...rest }: ButtonProps) {
    return (
        <StyledButton $kind={kind} $size={size} $full={full} $pill={pill} type={type} {...rest}>
            {icon && <Icon name={icon} size={size === 'lg' ? 20 : 17} stroke={2.2} />}
            {children}
        </StyledButton>
    )
}

type IconTone = 'quiet' | 'glass' | 'surface'
const StyledIconButton = styled.button<{ $tone: IconTone, $size: number }>`
    width: ${p => p.$size}px;
    height: ${p => p.$size}px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    ${p => p.$tone === 'glass' && css`background: rgba(255,255,255,0.16); color: #fff; border: 1px solid rgba(255,255,255,0.22);`}
    ${p => p.$tone === 'quiet' && css`background: ${m.color.surface2}; color: ${m.color.inkSoft}; border: none;`}
    ${p => p.$tone === 'surface' && css`background: ${m.color.surface}; color: ${m.color.ink}; border: none; box-shadow: ${m.shadow.card};`}
    &:focus-visible { outline: 2px solid ${m.color.primary}; outline-offset: 2px; }
`

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    name: string
    label: string
    tone?: IconTone
    size?: number
}
export function IconButton({ name, label, tone = 'quiet', size = 44, type = 'button', ...rest }: IconButtonProps) {
    return (
        <StyledIconButton $tone={tone} $size={size} type={type} aria-label={label} title={label} {...rest}>
            <Icon name={name} size={Math.round(size * 0.42)} stroke={2} />
        </StyledIconButton>
    )
}

// ── Textos ──────────────────────────────────────────────────────────
// Etiqueta de sección: Jost en mayúsculas espaciadas.
export const Label = styled.div`
    font-family: ${m.font.label};
    font-size: 11.5px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    color: ${m.color.inkFaint};
`

export const Card = styled.div<{ $pad?: number }>`
    background: ${m.color.surface};
    border-radius: ${m.radius.lg};
    padding: ${p => p.$pad ?? 14}px;
    box-shadow: ${m.shadow.card};
`

export const Badge = styled.span<{ $tone?: 'success' | 'warn' | 'neutral' }>`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 8px;
    border-radius: ${m.radius.pill};
    font-family: ${m.font.label};
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    white-space: nowrap;
    ${p => p.$tone === 'success' && css`color: ${m.color.success}; background: ${m.color.successSoft};`}
    ${p => p.$tone === 'warn' && css`color: ${m.color.warn}; background: ${m.color.warnSoft};`}
    ${p => (!p.$tone || p.$tone === 'neutral') && css`color: ${m.color.inkSoft}; background: ${m.color.primarySoft};`}
`

// ── Campos ──────────────────────────────────────────────────────────
const FieldWrap = styled.label`
    display: block;
    margin-bottom: 14px;
    min-width: 0;
    .control {
        display: flex;
        align-items: center;
        gap: 8px;
        height: 50px;
        padding: 0 13px;
        margin-top: 7px;
        position: relative;
        background: ${m.color.surface};
        border: 1.5px solid ${m.color.lineStrong};
        border-radius: ${m.radius.md};
        transition: border-color .15s ease;
        &:focus-within { border-color: ${m.color.primaryDeep}; }
    }
    input, select {
        flex: 1;
        min-width: 0;
        width: 100%;
        height: 100%;
        border: none;
        outline: none;
        background: transparent;
        font-family: ${m.font.text};
        /* 16px: con menos, iOS hace zoom al enfocar el campo. */
        font-size: 16px;
        font-weight: 500;
        color: ${m.color.ink};
        &::placeholder { color: ${m.color.inkFaint}; font-weight: 400; }
    }
    select {
        appearance: none;
        -webkit-appearance: none;
        cursor: pointer;
        padding-right: 24px;
    }
    .chevron {
        position: absolute;
        right: 13px;
        pointer-events: none;
        color: ${m.color.inkFaint};
    }
    .prefix { color: ${m.color.inkFaint}; font-weight: 600; }
    .hint {
        margin-top: 5px;
        font-family: ${m.font.text};
        font-size: 12px;
        color: ${m.color.inkFaint};
    }
`

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
    label: string
    prefix?: string
    hint?: string
    icon?: string
    // Sin etiqueta visible encima (el nombre queda solo para lectores de pantalla).
    hideLabel?: boolean
}
export function Field({ label, prefix, hint, icon, hideLabel, style, ...input }: FieldProps) {
    return (
        <FieldWrap style={style}>
            {!hideLabel && <Label as="span">{label}</Label>}
            <div className="control" style={hideLabel ? { marginTop: 0 } : undefined}>
                {icon && <span className="prefix"><Icon name={icon} size={18} /></span>}
                {prefix && <span className="prefix">{prefix}</span>}
                <input aria-label={label} {...input} />
            </div>
            {hint && <div className="hint">{hint}</div>}
        </FieldWrap>
    )
}

interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
    label: string
    options: { value: string, label: string }[]
}
export function SelectField({ label, options, style, ...select }: SelectFieldProps) {
    return (
        <FieldWrap style={style}>
            <Label as="span">{label}</Label>
            <div className="control">
                <select aria-label={label} {...select}>
                    {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <span className="chevron"><Icon name="chevronDown" size={18} stroke={2} /></span>
            </div>
        </FieldWrap>
    )
}

// Dos columnas que se apilan solas cuando no caben.
export const Row = styled.div`
    display: flex;
    gap: 12px;
    > * { flex: 1 1 0; }
`

// ── Segmented ───────────────────────────────────────────────────────
const SegmentedWrap = styled.div`
    display: flex;
    gap: 3px;
    padding: 3px;
    background: ${m.color.primarySoft};
    border-radius: ${m.radius.md};
    button {
        flex: 1;
        min-height: 44px;
        padding: 6px 8px;
        border: none;
        border-radius: 11px;
        background: transparent;
        color: ${m.color.inkSoft};
        font-family: ${m.font.text};
        font-weight: 600;
        font-size: 14px;
        cursor: pointer;
        transition: all .15s ease;
        -webkit-tap-highlight-color: transparent;
    }
    button[aria-pressed="true"] {
        background: ${m.color.surface};
        color: ${m.color.ink};
        box-shadow: ${m.shadow.card};
    }
`
// Dos (o más) botones separados, cada uno con su borde: para una elección que parte sin nada
// marcado, donde el control segmentado de arriba no se lee como botones.
const ChoiceWrap = styled.div`
    display: flex;
    gap: 10px;
    button {
        flex: 1;
        min-height: 52px;
        padding: 8px 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        border: 1.5px solid ${m.color.lineStrong};
        border-radius: ${m.radius.md};
        background: ${m.color.surface};
        color: ${m.color.ink};
        font-family: ${m.font.text};
        font-weight: 600;
        font-size: 14px;
        cursor: pointer;
        box-shadow: ${m.shadow.card};
        transition: all .15s ease;
        -webkit-tap-highlight-color: transparent;
    }
    .dot {
        width: 20px;
        height: 20px;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        border: 2px solid ${m.color.lineStrong};
        color: ${m.color.onPrimary};
    }
    button[aria-pressed="true"] {
        border-color: ${m.color.ink};
        background: ${m.color.ink};
        color: ${m.color.surface};
        .dot {
            border-color: ${m.color.primary};
            background: ${m.color.primary};
        }
    }
`
interface SegmentedProps<T> {
    options: { value: T, label: string }[]
    value: T | undefined
    onChange: (value: T) => void
    label: string
    variant?: 'segmented' | 'choice'
}
export function Segmented<T extends string | boolean>({ options, value, onChange, label, variant = 'segmented' }: SegmentedProps<T>) {
    const Wrap = variant === 'choice' ? ChoiceWrap : SegmentedWrap
    return (
        <Wrap role="group" aria-label={label}>
            {options.map(o => (
                <button key={String(o.value)} type="button" aria-pressed={o.value === value} onClick={() => onChange(o.value)}>
                    {variant === 'choice' && <span className="dot">{o.value === value && <Icon name="check" size={12} stroke={3} />}</span>}
                    {o.label}
                </button>
            ))}
        </Wrap>
    )
}

// ── Stepper ─────────────────────────────────────────────────────────
const StepperWrap = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    button {
        width: 46px;
        height: 46px;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1.5px solid ${m.color.lineStrong};
        border-radius: ${m.radius.md};
        background: ${m.color.surface};
        color: ${m.color.ink};
        cursor: pointer;
        &:disabled { opacity: 0.4; cursor: not-allowed; }
    }
    output {
        min-width: 30px;
        text-align: center;
        font-family: ${m.font.display};
        font-weight: 600;
        font-size: 28px;
        color: ${m.color.ink};
    }
`
export function Stepper({ value, onChange, min = 1, max = 20, label }: { value: number, onChange: (n: number) => void, min?: number, max?: number, label: string }) {
    return (
        <StepperWrap role="group" aria-label={label}>
            <button type="button" aria-label="Quitar uno" disabled={value <= min} onClick={() => onChange(Math.max(min, value - 1))}><Icon name="minus" size={18} stroke={2.4} /></button>
            <output aria-live="polite">{value}</output>
            <button type="button" aria-label="Agregar uno" disabled={value >= max} onClick={() => onChange(Math.min(max, value + 1))}><Icon name="plus" size={18} stroke={2.4} /></button>
        </StepperWrap>
    )
}

// ── Avatar e identidad ──────────────────────────────────────────────
const AV_TINTS: [string, string][] = [
    ['#E7D2C4', '#7A4A2E'], ['#D2E4DF', '#1E6E66'], ['#DCE0F2', '#33408F'],
    ['#EBDCC0', '#7E5A1C'], ['#E3D6E6', '#6B3F73'], ['#D6E5CF', '#3F6B2E'],
]
export function Avatar({ name, size = 32 }: { name: string, size?: number }) {
    const clean = (name || '?').trim()
    const parts = clean.split(/\s+/)
    const initials = ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase()
    let h = 0
    for (const c of clean) h = (h * 31 + c.charCodeAt(0)) % 997
    const [bg, fg] = AV_TINTS[h % AV_TINTS.length]
    return (
        <span aria-hidden="true" style={{
            width: size, height: size, borderRadius: '50%', background: bg, color: fg, flexShrink: 0,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: m.font.text, fontWeight: 700, fontSize: size * 0.36,
        }}>{initials}</span>
    )
}

export const CountryCode = styled.span`
    font-family: ${m.font.label};
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.06em;
    color: ${m.color.inkSoft};
    background: ${m.color.primarySoft};
    padding: 2px 5px;
    border-radius: 6px;
    line-height: 1.2;
`

// Cuadro con el degradado del tour y su pictograma blanco.
export function TourThumb({ tourId, size = 46 }: { tourId: string, size?: number }) {
    const { picto, tone } = tourVisual(tourId)
    return (
        <span aria-hidden="true" style={{
            width: size, height: size, flexShrink: 0, borderRadius: size > 30 ? 14 : 7,
            background: `linear-gradient(135deg, ${tone[0]}, ${tone[1]})`,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}>
            <img src={picto} alt="" style={{ height: Math.round(size * 0.64), maxWidth: '86%', objectFit: 'contain' }} />
        </span>
    )
}

// ── Tira de días ────────────────────────────────────────────────────
const DayStripWrap = styled.div<{ $glass?: boolean }>`
    position: relative;
    .strip-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        margin-bottom: 8px;
    }
    .strip-month {
        font-family: ${m.font.display};
        font-weight: 600;
        font-size: 16px;
        text-transform: capitalize;
        color: ${p => p.$glass ? '#fff' : m.color.ink};
    }
    .strip-controls {
        display: flex;
        gap: 6px;
    }
    .days {
        display: flex;
        gap: 6px;
    }
    .days button {
        flex: 1 1 0;
        min-width: 0;
        min-height: 56px;
        padding: 8px 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 2px;
        border: none;
        border-radius: ${m.radius.md};
        background: ${p => p.$glass ? 'rgba(255,255,255,0.14)' : m.color.surface};
        color: ${p => p.$glass ? '#fff' : m.color.ink};
        cursor: pointer;
        -webkit-tap-highlight-color: transparent;
        &:disabled {
            opacity: 0.4;
            cursor: not-allowed;
        }
    }
    .dow {
        font-family: ${m.font.label};
        font-size: 10px;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        color: ${p => p.$glass ? 'rgba(255,255,255,0.6)' : m.color.inkFaint};
    }
    .num {
        font-family: ${m.font.display};
        font-weight: 600;
        font-size: 18px;
    }
    .days button[aria-pressed="true"] {
        background: ${p => p.$glass ? '#fff' : m.color.ink};
        color: ${p => p.$glass ? m.color.ink : m.color.surface};
        box-shadow: ${m.shadow.card};
        .dow { color: ${p => p.$glass ? m.color.inkSoft : 'rgba(255,255,255,0.75)'}; }
    }
`

// Semana visible de 7 días: parte en ayer (o en hoy si no se permiten fechas pasadas) y, si el
// día elegido cae fuera, se centra en él. Las flechas avanzan o retroceden una semana y el botón
// del calendario abre el mes completo para saltar a cualquier fecha.
export function DayStrip({ day, onChange, glass, allowPast = true }: { day: Date | undefined, onChange: (d: Date) => void, glass?: boolean, allowPast?: boolean }) {
    const today = startOfToday()
    const first = add(today, { days: allowPast ? -1 : 0 })
    const startFor = (d: Date | undefined) => {
        if (!d || (d >= first && d <= add(first, { days: 6 }))) return first
        const centered = add(d, { days: -3 })
        return !allowPast && centered < today ? today : centered
    }
    const [start, setStart] = useState<Date>(() => startFor(day))
    const [calendarOpen, setCalendarOpen] = useState(false)
    const dayTime = day?.getTime()

    // Si el día cambia desde afuera (o desde el calendario) y queda fuera de la semana visible, la
    // tira se mueve hasta él.
    useEffect(() => {
        if (dayTime === undefined) return
        setStart(current => (dayTime < current.getTime() || dayTime > add(current, { days: 6 }).getTime()) ? startFor(new Date(dayTime)) : current)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dayTime])

    const days = Array.from({ length: 7 }, (_, i) => add(start, { days: i }))
    const canGoBack = allowPast || start > today
    const tone: 'glass' | 'surface' = glass ? 'glass' : 'surface'

    function move(weeks: number) {
        const next = add(start, { days: weeks * 7 })
        setStart(!allowPast && next < today ? today : next)
    }

    return (
        <DayStripWrap $glass={glass}>
            <div className="strip-head">
                <span className="strip-month">{format(days[3], 'LLLL yyyy', { locale: es })}</span>
                <div className="strip-controls">
                    <IconButton name="chevronLeft" label="Semana anterior" tone={tone} size={40} disabled={!canGoBack} onClick={() => move(-1)} />
                    <IconButton name="chevronRight" label="Semana siguiente" tone={tone} size={40} onClick={() => move(1)} />
                    <IconButton name="calendar" label="Abrir calendario" tone={tone} size={40} aria-expanded={calendarOpen} onClick={() => setCalendarOpen(!calendarOpen)} />
                </div>
                {calendarOpen && (
                    <RangeCalendar
                        single
                        align="right"
                        from={day || today}
                        min={allowPast ? undefined : today}
                        onChange={onChange}
                        onClose={() => setCalendarOpen(false)}
                    />
                )}
            </div>
            <div className="days">
                {days.map(d => (
                    <button key={d.toISOString()} type="button" aria-pressed={!!day && isSameDay(d, day)} aria-label={longDate(d)}
                        disabled={!allowPast && d < today} onClick={() => onChange(d)}>
                        <span className="dow">{format(d, 'EEE', { locale: es })}</span>
                        <span className="num">{format(d, 'd')}</span>
                    </button>
                ))}
            </div>
        </DayStripWrap>
    )
}

// ── Wizard ──────────────────────────────────────────────────────────
const slideUp = keyframes`
    from { transform: translateY(24px); opacity: 0; }
    to { transform: none; opacity: 1; }
`
const WizardOverlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 100;
    background: ${m.color.bg};
    ${desktop} {
        background: rgba(43,38,32,0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 24px;
    }
`
const WizardForm = styled.form`
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    background: ${m.color.bg};
    font-family: ${m.font.text};
    color: ${m.color.ink};
    animation: ${slideUp} .32s cubic-bezier(.25,.9,.3,1);
    ${desktop} {
        position: relative;
        inset: auto;
        width: 520px;
        height: min(760px, 100%);
        border-radius: ${m.radius.lg};
        overflow: hidden;
        box-shadow: ${m.shadow.modal};
    }
    .wizard-head {
        padding: 18px 18px 16px;
        background: ${m.color.ink};
        color: ${m.color.surface};
    }
    .wizard-title {
        display: flex;
        align-items: center;
        gap: 12px;
    }
    .wizard-step {
        font-family: ${m.font.label};
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        color: rgba(255,255,255,0.55);
    }
    h1 {
        margin: 2px 0 0;
        font-family: ${m.font.display};
        font-weight: 600;
        font-size: 21px;
        color: ${m.color.surface};
    }
    .wizard-progress {
        display: flex;
        gap: 6px;
        margin-top: 14px;
        span {
            flex: 1;
            height: 5px;
            border-radius: ${m.radius.pill};
            background: rgba(255,255,255,0.18);
        }
        span.on { background: ${m.color.primary}; }
    }
    .wizard-body {
        flex: 1;
        overflow-y: auto;
        padding: 16px 18px 24px;
        -webkit-overflow-scrolling: touch;
    }
    .wizard-foot {
        padding: 12px 18px calc(16px + env(safe-area-inset-bottom));
        background: ${m.color.bg};
        border-top: 1px solid ${m.color.line};
        display: flex;
        flex-direction: column;
        gap: 8px;
    }
`

interface WizardShellProps {
    step: number
    title: string
    // Cómo se llama el flujo en el encabezado ("Agregar huéspedes", "Agregar tour").
    flow?: string
    onClose: () => void
    // 'back' muestra la flecha en vez de la X (no se sale del flujo, se vuelve un paso).
    closeIcon?: 'x' | 'chevronLeft'
    closeLabel?: string
    onSubmit?: (e: FormEvent<HTMLFormElement>) => void
    footer: ReactNode
    children: ReactNode
}
export function WizardShell({ step, title, flow = 'Agregar huéspedes', onClose, closeIcon = 'x', closeLabel = 'Cerrar', onSubmit, footer, children }: WizardShellProps) {
    return (
        <WizardOverlay>
            <WizardForm onSubmit={onSubmit} noValidate>
                <div className="wizard-head">
                    <div className="wizard-title">
                        <IconButton name={closeIcon} label={closeLabel} tone="glass" size={40} onClick={onClose} />
                        <div>
                            <span className="wizard-step">{flow} · Paso {step} de 3</span>
                            <h1>{title}</h1>
                        </div>
                    </div>
                    <div className="wizard-progress" aria-hidden="true">
                        {[1, 2, 3].map(i => <span key={i} className={i <= step ? 'on' : ''} />)}
                    </div>
                </div>
                <div className="wizard-body">{children}</div>
                <div className="wizard-foot">{footer}</div>
            </WizardForm>
        </WizardOverlay>
    )
}

// Aviso dentro de una pantalla (no flotante): algo que falta, o que todavía no está disponible.
export const Notice = styled.div<{ $tone?: 'warn' | 'info' }>`
    display: flex;
    align-items: flex-start;
    gap: 9px;
    padding: 12px 14px;
    border-radius: ${m.radius.md};
    font-family: ${m.font.text};
    font-size: 13.5px;
    line-height: 1.4;
    ${p => p.$tone === 'warn'
        ? css`background: ${m.color.warnSoft}; color: ${m.color.warn};`
        : css`background: ${m.color.surface2}; color: ${m.color.inkSoft};`}
    svg { margin-top: 1px; }
`

// ── Diálogo ─────────────────────────────────────────────────────────
// Ventana del portal para lo que no es un paso del wizard (resumen, editar, borrar, detalle): en
// celular sube desde abajo; en desktop va centrada.
const DialogOverlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 100;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    background: rgba(43,38,32,0.5);
    ${desktop} {
        align-items: center;
        padding: 24px;
    }
`
const DialogPanel = styled.form<{ $wide?: boolean }>`
    width: 100%;
    max-width: ${p => p.$wide ? '640px' : '480px'};
    max-height: 92dvh;
    display: flex;
    flex-direction: column;
    background: ${m.color.bg};
    border-radius: 28px 28px 0 0;
    box-shadow: ${m.shadow.modal};
    font-family: ${m.font.text};
    color: ${m.color.ink};
    animation: ${slideUp} .28s cubic-bezier(.25,.9,.3,1);
    ${desktop} {
        max-height: min(760px, 100%);
        border-radius: ${m.radius.lg};
    }
    .dialog-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 18px 20px 12px;
    }
    h1 {
        margin: 0;
        font-family: ${m.font.display};
        font-weight: 600;
        font-size: 22px;
        color: ${m.color.ink};
    }
    .dialog-body {
        overflow-y: auto;
        padding: 4px 20px 20px;
        -webkit-overflow-scrolling: touch;
    }
    .dialog-foot {
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 12px 20px calc(18px + env(safe-area-inset-bottom));
        border-top: 1px solid ${m.color.line};
    }
`
interface DialogProps {
    title: string
    onClose: () => void
    onSubmit?: (e: FormEvent<HTMLFormElement>) => void
    footer?: ReactNode
    wide?: boolean
    children: ReactNode
}
export function Dialog({ title, onClose, onSubmit, footer, wide, children }: DialogProps) {
    return (
        <DialogOverlay>
            <DialogPanel $wide={wide} role="dialog" aria-label={title} noValidate onSubmit={onSubmit || (e => e.preventDefault())}>
                <div className="dialog-head">
                    <h1>{title}</h1>
                    <IconButton name="x" label="Cerrar" tone="surface" size={40} onClick={onClose} />
                </div>
                <div className="dialog-body">{children}</div>
                {footer && <div className="dialog-foot">{footer}</div>}
            </DialogPanel>
        </DialogOverlay>
    )
}
